import React, { useState, useEffect } from 'react';

interface AdminActionPasswordModalProps {
  isOpen: boolean;
  title: string;
  description?: string;
  onConfirm: (password: string) => Promise<void>;
  onCancel: () => void;
}

const AdminActionPasswordModal: React.FC<AdminActionPasswordModalProps> = ({
  isOpen,
  title,
  description,
  onConfirm,
  onCancel,
}) => {
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Reset state each time the modal opens
  useEffect(() => {
    if (isOpen) {
      setPassword('');
      setError('');
      setLoading(false);
      setShowPassword(false);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!password.trim()) {
      setError('Vui lòng nhập mật khẩu xác nhận.');
      return;
    }
    setLoading(true);
    setError('');
    try {
      await onConfirm(password.trim());
    } catch (err: any) {
      setError(err.message || 'Lỗi không xác định.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="modal-overlay"
      onClick={(e) => { if (e.target === e.currentTarget) onCancel(); }}
    >
      <div className="modal-content" style={{ maxWidth: 420 }}>
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
          <span style={{ fontSize: '1.5rem' }}>🔐</span>
          <h3 style={{ margin: 0, fontSize: '1.1rem', color: '#1e293b' }}>{title}</h3>
        </div>

        {description && (
          <p style={{ color: '#64748b', fontSize: '0.9rem', margin: '0 0 16px 0' }}>
            {description}
          </p>
        )}

        <form onSubmit={handleSubmit} className="admin-form">
          <div className="form-group">
            <label style={{ fontWeight: 600, fontSize: '0.9rem' }}>
              Mật khẩu xác nhận Admin
            </label>
            <div style={{ position: 'relative' }}>
              <input
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Nhập mật khẩu xác nhận..."
                autoFocus
                style={{
                  width: '100%',
                  padding: '10px 40px 10px 12px',
                  border: error ? '1.5px solid #ef4444' : '1.5px solid #cbd5e1',
                  borderRadius: 8,
                  fontSize: '0.95rem',
                  outline: 'none',
                  boxSizing: 'border-box',
                }}
              />
              <button
                type="button"
                onClick={() => setShowPassword(v => !v)}
                style={{
                  position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)',
                  background: 'none', border: 'none', cursor: 'pointer', fontSize: '1rem',
                  color: '#64748b', padding: 0
                }}
                tabIndex={-1}
              >
                {showPassword ? '🙈' : '👁️'}
              </button>
            </div>
            {error && (
              <p style={{ color: '#ef4444', fontSize: '0.82rem', margin: '6px 0 0 0' }}>
                ⚠️ {error}
              </p>
            )}
          </div>

          <div className="modal-actions" style={{ marginTop: 20 }}>
            <button
              type="button"
              className="btn"
              onClick={onCancel}
              disabled={loading}
            >
              Hủy
            </button>
            <button
              type="submit"
              className="btn btn-danger"
              disabled={loading || !password.trim()}
            >
              {loading ? '⏳ Đang xử lý...' : '✅ Xác nhận'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AdminActionPasswordModal;
